import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    TextField,
    IconButton,
    Chip,
    Tooltip,
    Modal,
    CircularProgress
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ReceiptLong as TariffIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { productService } from '../../../services/productService';
import ConfirmDialog from '../../../components/ConfirmDialog';
import ProductForm from './ProductForm';
import TariffDialog from '../../../components/tariff/TariffDialog';

export default function ProductList({ onDataChange }) {
    const { handleApiCall, showSuccess, showError } = useApiHandler();
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0
    });
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [tariffProduct, setTariffProduct] = useState(null);

    useEffect(() => {
        fetchProducts();
    }, [page, rowsPerPage]);

    useEffect(() => {
        if (searchTerm !== '') {
            setPage(0);
            setPagination(prev => ({ ...prev, current_page: 1 }));
        }
        
        const timeoutId = setTimeout(() => {
            fetchProducts();
        }, searchTerm ? 300 : 0); // Debounce only for search

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    const fetchProducts = async () => {
        try {
            const params = {
                page: page + 1, // Convert 0-based to 1-based
                per_page: rowsPerPage
            };
            
            if (searchTerm) params.search = searchTerm;

            const response = await productService.getAllProducts(params);
            if (response.success) {
                setProducts(response.data);
                if (response.pagination) {
                    setPagination(response.pagination);
                }
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            showError('Failed to fetch products');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleAdd = () => {
        setEditingProduct(null);
        setIsModalVisible(true);
    };

    const handleEdit = async (product) => {
        try {
            const response = await productService.getProduct(product.id);
            if (response.success) {
                setEditingProduct(response.data);
                        setIsModalVisible(true);
            }
        } catch (error) {
            console.error('Error loading product:', error);
            showError('Failed to load product details');
        }
    };

    const handleDelete = async () => {
        await handleApiCall(
            productService.deleteProduct(deleteDialog.id),
            {
                successMessage: 'Product deleted successfully',
                errorMessage: 'Failed to delete product',
                onSuccess: () => {
                    fetchProducts();
                    if (onDataChange) onDataChange();
                }
            }
        );
        setDeleteDialog({ open: false, id: null });
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setEditingProduct(null);
    };

    const handleFormSuccess = () => {
        setIsModalVisible(false);
        setEditingProduct(null);
        fetchProducts();
        if (onDataChange) onDataChange();
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Products</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                >
                    NEW PRODUCT
                </Button>
            </Box>

            {/* Search */}
            <Box sx={{ p: 2 }}>
                <TextField
                    fullWidth
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={handleSearch}
                    size="small"
                />
            </Box>

            {/* Table */}
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Code</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Tax Calculation</TableCell>
                            <TableCell>Default Weight Range</TableCell>
                            <TableCell>Default Zone Rates</TableCell>
                            <TableCell>Additional Ranges</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {products.map((product) => (
                            <TableRow key={product.id}>
                                <TableCell>{product.name}</TableCell>
                                <TableCell>{product.code}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={product.type ? 'Document' : 'Non-Document'}
                                        color={product.type ? 'primary' : 'secondary'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={product.tax_calculation_type === 'inclusive' ? 'Inclusive' : 'Exclusive'}
                                        color={product.tax_calculation_type === 'inclusive' ? 'success' : 'warning'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    {product.default_weight_from} - {product.default_weight_to} kg
                                </TableCell>
                                <TableCell>
                                    {/* Shown against the total so a partly-priced
                                        product is obvious. A destination with no
                                        rate cannot be quoted at all. */}
                                    <Chip
                                        size="small"
                                        label={
                                            product.zones_available
                                                ? `${product.default_zone_rates_count || 0} of ${product.zones_available}`
                                                : `${product.default_zone_rates_count || 0} zones`
                                        }
                                        color={
                                            product.zones_available &&
                                            product.default_zone_rates_count >= product.zones_available
                                                ? 'success'
                                                : 'warning'
                                        }
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell>
                                    {product.weight_ranges_count || 0} ranges
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="View tariff (per-zone prices)">
                                        <IconButton
                                            onClick={() => setTariffProduct(product)}
                                            size="small"
                                            color="primary"
                                        >
                                            <TariffIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit">
                                        <IconButton
                                            onClick={() => handleEdit(product)}
                                            size="small"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton
                                            onClick={() => setDeleteDialog({ open: true, id: product.id })}
                                            color="error"
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {products.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={9} align="center">
                                    No products found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
                component="div"
                count={pagination.total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                }
            />

            {/* Product Form Modal */}
            <Modal
                open={isModalVisible}
                onClose={handleModalClose}
                aria-labelledby="product-modal-title"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '90%',
                    maxWidth: 800,
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 4,
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}>
                    <Typography id="product-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                        {editingProduct ? 'Edit' : 'Add'} Product
                    </Typography>
                    <ProductForm
                        product={editingProduct}
                        onSuccess={handleFormSuccess}
                        onCancel={handleModalClose}
                    />
                </Box>
            </Modal>

            {/* Delete Confirmation Dialog */}
            <TariffDialog
                open={Boolean(tariffProduct)}
                onClose={() => setTariffProduct(null)}
                title={`Tariff: ${tariffProduct?.name || ''}`}
                subtitle={tariffProduct?.code}
                filename={`sunex-tariff-${(tariffProduct?.code || 'package').replace(/\s+/g, '-').toLowerCase()}.pdf`}
                emptyMessage="This package has no zone prices configured yet."
                fetcher={async () => {
                    const response = await productService.getTariff(tariffProduct.id);
                    // The dialog always works with a list of sheets; a package is a list of one.
                    return response.success ? [response.data] : [];
                }}
            />

            <ConfirmDialog
                open={deleteDialog.open}
                title="Delete Product"
                content="Are you sure you want to delete this product? This action cannot be undone."
                onConfirm={handleDelete}
                onClose={() => setDeleteDialog({ open: false, id: null })}
            />
        </Box>
    );
} 